import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "moment";
import { includes, isArray, isObject } from "lodash";
import { isDynamicDateRange } from "@/services/parameters/DateRangeParameter";
import DateRangeInput from "@/components/DateRangeInput";
import DateTimeRangeInput from "@/components/DateTimeRangeInput";
import DynamicButton from "@/components/dynamic-parameters/DynamicButton";
import notification from "@/services/notification";

import "./DynamicParameters.less";

function isValidDateRangeValue(value) {
  return isArray(value) && value.length === 2 && moment.isMoment(value[0]) && moment.isMoment(value[1]);
}

class DynamicDateRangePicker extends React.Component {
  static propTypes = {
    type: PropTypes.oneOf(["date-range", "datetime-range","datetime-range-with-minutes", "datetime-range-with-seconds"]).isRequired,
    className: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    parameter: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    onSelect: PropTypes.func,
    dynamicButtonOptions: PropTypes.shape({
      staticValueLabel: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.object,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        })
      ),
    }),
    dateRangeOptions: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    type: "date-range",
    className: "",
    value: null,
    parameter: null,
    dynamicButtonOptions: {
      options: [],
    },
    onSelect: () => {},
  };

  constructor(props) {
    super(props);
    this.dateRangeComponentRef = React.createRef();
  }

  onDynamicValueSelect = dynamicValue => {
    try {
      const { onSelect, parameter } = this.props;
      if (dynamicValue === "static") {
        const parameterValue = parameter && parameter.getExecutionValue ? parameter.getExecutionValue() : null;
        if (isObject(parameterValue) && parameterValue.start && parameterValue.end) {
          onSelect([moment(parameterValue.start), moment(parameterValue.end)]);
        } else {
          onSelect(null);
        }
      } else {
        // DynamicButton passes option objects. For date range presets, actual dynamic
        // object is usually nested as option.value (which itself has a .value() function).
        const dynamicObject =
          dynamicValue && dynamicValue.value && typeof dynamicValue.value.value === "function"
            ? dynamicValue.value
            : dynamicValue;
        const computedRange = dynamicObject && typeof dynamicObject.value === "function" ? dynamicObject.value() : null;
        if (!isArray(computedRange) || computedRange.length !== 2) {
          notification.warning("Invalid dynamic date range value");
          return;
        }
        const [start, end] = computedRange;
        if (!start?.isValid?.() || !end?.isValid?.()) {
          notification.warning("Invalid dynamic date range value");
          return;
        }

        const withinBounds = parameter?.isWithinBounds ? parameter.isWithinBounds(start, end) : true;
        if (!withinBounds) {
          const { minDate, maxDate } = parameter?.getBounds ? parameter.getBounds() : { minDate: null, maxDate: null };
          const minText = minDate && minDate.isValid() ? minDate.format("YYYY-MM-DD HH:mm:ss") : "not set";
          const maxText = maxDate && maxDate.isValid() ? maxDate.format("YYYY-MM-DD HH:mm:ss") : "not set";
          notification.warning(
            "Selected dynamic date range is outside allowed range",
            `Allowed slot: ${minText} to ${maxText}.`
          );
          return;
        }

        onSelect(dynamicObject);
      }
    } catch (error) {
      notification.error("Unable to apply dynamic date range", String(error));
    } finally {
      // give focus to the DatePicker to get keyboard shortcuts to work
      if (this.dateRangeComponentRef.current && this.dateRangeComponentRef.current.focus) {
        this.dateRangeComponentRef.current.focus();
      }
    }
  };

  render() {
    const { type, value, onSelect, className, dynamicButtonOptions, dateRangeOptions, parameter, ...rest } = this.props;
    const isDateTimeRange = includes(type, "datetime-range");
    const hasDynamicValue = isDynamicDateRange(value);

    const additionalAttributes = {};

    let DateRangeComponent = DateRangeInput;
    if (isDateTimeRange) {
      DateRangeComponent = DateTimeRangeInput;
      if (includes(type, "with-seconds")) {
        additionalAttributes.withSeconds = true;
        additionalAttributes.withMinutes = true;
      }

      else if (includes(type, "with-minutes")) {
        additionalAttributes.withMinutes = true;
        additionalAttributes.withSeconds = false;
      }
    }

    if (isValidDateRangeValue(value) || value === null) {
      additionalAttributes.value = value;
    }

    const parameterMinDate = parameter?.minDate ? moment(parameter.minDate, moment.ISO_8601, true) : null;
    const parameterMaxDate = parameter?.maxDate ? moment(parameter.maxDate, moment.ISO_8601, true) : null;
    const minDate = parameterMinDate && parameterMinDate.isValid() ? parameterMinDate : null;
    const maxDate = parameterMaxDate && parameterMaxDate.isValid() ? parameterMaxDate : null;
    if (minDate || maxDate) {
      additionalAttributes.disabledDate = current => {
        if (!current) return false;
        if (minDate && current.isBefore(minDate, "day")) return true;
        if (maxDate && current.isAfter(maxDate, "day")) return true;
        return false;
      };
    }

    if (hasDynamicValue) {
      additionalAttributes.placeholder = [value && value.name];
      additionalAttributes.value = null;
    }

    return (
      <div {...rest} className={classNames("date-range-parameter", className)}>
        <DateRangeComponent
          {...dateRangeOptions}
          ref={this.dateRangeComponentRef}
          className={classNames("redash-datepicker date-range-input", type, { "dynamic-value": hasDynamicValue })}
          onSelect={onSelect}
          suffixIcon={null}
          {...additionalAttributes}
        />
        <DynamicButton
          options={dynamicButtonOptions.options}
          staticValueLabel={dynamicButtonOptions.staticValueLabel}
          selectedDynamicValue={hasDynamicValue ? value : null}
          enabled={hasDynamicValue}
          onSelect={this.onDynamicValueSelect}
        />
      </div>
    );
  }
}

export default DynamicDateRangePicker;
